// Besoins en bénévoles des matchs, par équipe (D-119), côté bureau :
//  - Club › Bénévolat › Besoins des matchs (D-127 ; avant : l'onglet Bénévolat › Matchs) : les besoins du club pour chaque
//    match à domicile (un nombre par besoin, retirer, ajouter),
//    la suggestion selon le niveau (une à la fois, « Oui » / « Non merci »), les équipes engagées et leurs puces ;
//  - la fiche d'une équipe : « Club · n », « Autre », « Aucun » par besoin, la condition « 2 matchs ou plus au gymnase »,
//    un besoin pour elle seule ; « Enregistrer » montre ce qui a changé et les créneaux déjà pris (« Le garder »,
//    « Retirer et prévenir <prénom> ») ;
//  - sur la carte d'un match (Créneaux) : « Modifier les besoins » (seulement ce match), « Inscrire quelqu'un » sur un
//    besoin ouvert, et un prénom inscrit à toucher pour le « Retirer avec un merci » ;
//  - la fiche de match du coach : les besoins de son match, en lecture.
// La base applique et prévient (supabase/migrations/20261102300000_besoins_matchs.sql) ; les textes sont les siens
// (src/domain/besoins.ts). Tutoiement ; accords via agree() et le genre du besoin.
import './besoins.css';
import {
  addSignup, answerHint, dropSlot, matchPlan, needsBoard, saveClubNeeds, saveMatchNeeds, saveUnitRules, slotCandidates, slotPeople, thankSignup,
  type ApplyReport, type Candidate, type KeptSlot, type NeedsBoard, type SlotPerson,
} from '../lib/besoins-api';
import {
  addedMessage, agreeNoun, busyLine, changeSummary, deName, frDay, hintFor, hintText, homeCount, isMini, keptWho, labelForms, lowerFirst, matchWord,
  NEED_LABELS, ruleChips, ruleOf, ruleWhy, sameLabel, storedRules, thankMessage, unitKey, unitName, unitRank, unitShort,
  type ClubNeed, type HintKind, type RuleMode, type ThankReason, type Unit, type UnitRule,
} from '../domain/besoins';
import { levelFromCompetition, levelKey, type LevelKey } from '../domain/rt';
import { agree, type TeamGender } from '../domain/labels';
import { localDate } from '../domain/schedule';
import { clubVolunteerNeeds, type VolunteerNeed } from '../lib/api';
import { time } from './format';
import type { Ctx } from './context';
import { $, $$, html, mount, openSheet, toast, type Html } from './dom';
import { guestBlock, openGuestRemove, wireGuest } from './besoins-sans-app';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const longDay = (d: string) => cap(frDay(d));
const firstOf = (n: string) => n.trim().split(/\s+/)[0] ?? n;
/** Un besoin d'officiel (table, chrono, 24 secondes, arbitre) : demandé à chaque match, sans condition proposée. */
const OFFICIAL = /(table de marque|e-?marque|chrono|24 ?s|arbitr|officiel)/i;
const genderOf = (people: { g: string }[]): TeamGender => (people.length && people.every(p => p.g === 'F') ? 'F' : 'M');
const names = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`);
const REASON_LABEL: Record<ThankReason, string> = { comble: 'Déjà comblé', plus_besoin: 'Plus besoin' };
const whenLine = (n: Pick<VolunteerNeed, 'occurrence_date' | 'starts_at'>) => `${longDay(n.occurrence_date)}${n.starts_at ? ` · ${time(new Date(n.starts_at))}` : ''}`;

/** Un compteur −/+ ; `key` repère le nombre réglé. Au minimum, « − » est grisé (sauf `minAct` : il propose de retirer). */
const stepper = (n: number, min: number, key: string, label: string, minAct = false): Html => html`
  <span class="bm-step" role="group" aria-label="${label}">
    <button type="button" data-dec="${key}" aria-label="${n <= min && minAct ? `Retirer ${label}` : `Moins : ${label}`}" ${n <= min && !minAct ? 'disabled' : ''}>−</button><b>${n}</b><button type="button" data-inc="${key}" aria-label="Plus : ${label}" ${n >= 20 ? 'disabled' : ''}>+</button>
  </span>`;

/** Les erreurs de la base, dites simplement. */
function problem(e: unknown, fallback = 'Impossible pour le moment.'): string {
  const m = String((e as Error)?.message ?? '');
  if (m.includes('complet')) return 'Ce créneau vient d’être complété.';
  if (m.includes('réservé aux adultes')) return 'Créneau réservé aux adultes : ouvre-le d’abord aux jeunes.';
  if (m.includes('passé')) return 'Ce créneau est passé.';
  if (m.includes('annulé')) return 'Ce match est annulé.';
  if (m.includes('duplicate') || m.includes('déjà')) return 'Déjà inscrit sur ce créneau.';
  return fallback;
}

// -----------------------------------------------------------------------------
// Club › Bénévolat › Besoins des matchs (D-127)
// -----------------------------------------------------------------------------
interface UnitView extends Unit { key: string; level: string | null; lk: LevelKey | null; mini: boolean; rules: UnitRule[] }

function unitsOf(b: NeedsBoard): UnitView[] {
  return b.units.map(u => {
    const level = u.squad_level ?? levelFromCompetition(u.squad_comp) ?? u.team_level ?? levelFromCompetition(u.team_comp);
    const lk = levelKey(level, u.category);
    const rules = b.rules.filter(r => r.team_id === u.team_id && (r.squad_id ?? null) === (u.squad_id ?? null)).sort((x, y) => x.position - y.position)
      .map(({ label, mode, needed, only_multi, extra, position }) => ({ label, mode, needed, only_multi, extra, position }));
    return { ...u, key: unitKey(u), level, lk, mini: isMini(lk), rules };
  }).sort((x, y) => unitRank(y) - unitRank(x));
}
const levelText = (u: UnitView) => u.level ?? (u.mini ? 'Plateaux mini-basket' : '');

/** La première suggestion pas encore tranchée (une à la fois). */
function pendingHint(b: NeedsBoard, units: UnitView[], roles: ClubNeed[]): { u: UnitView; kind: HintKind; labels: string[] } | null {
  for (const u of units) {
    const h = hintFor(u.lk, roles, u.rules);
    if (h && !b.hints.some(x => x.team_id === u.team_id && (x.squad_id ?? null) === (u.squad_id ?? null) && x.kind === h.kind)) return { u, ...h };
  }
  return null;
}

/** Les besoins des matchs du bureau (route #/club/benevolat/besoins, D-127 ; #/benevoles/matchs y mène). */
export async function renderMatchNeeds(el: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club;
  if (!club) return;
  const b = await needsBoard(club.id).catch(() => null);
  if (!b) { mount(el, html`<div class="empty"><p class="empty-title">Réservé au bureau.</p></div>`); return; }
  const reload = () => renderMatchNeeds(el, ctx);
  const units = unitsOf(b);
  const roles: ClubNeed[] = b.roles.map(r => ({ label: r.label, needed: r.needed }));
  let timer = 0, what = '', queue: Promise<unknown> = Promise.resolve();

  // Les besoins du club : chaque geste s'enregistre (regroupés), puis s'applique aux matchs à domicile à venir.
  const save = (label: string) => {
    what = label;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const said = what;
      queue = queue.then(async () => {
        try {
          const r = await saveClubNeeds(club.id, roles);
          b.default = false; draw();
          if (r.kept.length) openReport('Besoins du club enregistrés', r, said, false, () => void reload());
          else toast(r.matches ? `${said} · ${homeCount(r.matches, false)} mis à jour` : `${said} · enregistré`);
        } catch { toast('Enregistrement impossible. Réessaie.', 'ko'); await reload(); }
      });
    }, 450);
  };

  const draw = () => {
    const sug = pendingHint(b, units, roles);
    const t = sug ? hintText(sug.kind, unitShort(sug.u), sug.u.level, sug.labels) : null;
    mount(el, html`
      <p class="muted bm-intro">Chaque match à domicile crée tout seul ses besoins. Règle ici ce qu’il demande, et ce qui change pour une équipe.</p>
      <section class="block" data-guide="bm-club">
        <h3>Pour chaque match à domicile</h3>
        ${b.default ? html`<p class="hint">Les besoins par défaut : change un nombre ou ajoute un besoin, ils deviennent ceux du club.</p>` : ''}
        <ul class="bm-needs">${roles.map((r, i) => html`<li><strong>${r.label}</strong>${stepper(r.needed, 1, String(i), r.label, true)}</li>`)}</ul>
        <button type="button" class="btn btn-ghost btn-sm bm-add" id="bm-add-need">＋ Ajouter un besoin</button>
      </section>
      ${sug && t ? html`
        <div class="bm-sug" data-guide="bm-sug">
          <p class="eyebrow">Suggestion · niveau FFBB</p>
          <p><strong>${t.lead}</strong></p>
          <p class="muted small">${t.ask}</p>
          <div class="btn-row"><button type="button" class="btn btn-primary btn-sm" id="bm-sug-yes">${t.yes}</button><button type="button" class="btn btn-ghost btn-sm" id="bm-sug-no">Non merci</button></div>
        </div>` : ''}
      <section class="block">
        <h3>Par équipe</h3>
        ${units.length ? html`
          <ul class="bm-teams">${units.map(u => {
            const chips = ruleChips(roles, u.rules);
            return html`<li><button type="button" data-unit="${u.key}" data-guide="bm-team">
              <span class="bm-t"><span><strong>${unitName(u)}</strong><span class="muted small">${levelText(u)}</span></span>
                <span class="bm-chips">${chips.length ? chips.map(c => html`<span class="chip chip-mute">${c}</span>`) : html`<span class="chip chip-soft">Comme le club</span>`}</span></span>
              <span class="bm-go" aria-hidden="true">›</span></button></li>`;
          })}</ul>
          <p class="hint">Les règles valent pour les matchs à venir. Un créneau déjà pris n’est jamais retiré sans te prévenir.</p>`
        : html`<p class="muted small">Pas encore d’équipe dans la saison en cours : les équipes se créent dans Club › Équipes.</p>`}
      </section>`);

    for (const btn of $$<HTMLButtonElement>(el, '[data-inc]')) btn.addEventListener('click', () => {
      const r = roles[Number(btn.dataset.inc)]!;
      if (r.needed >= 20) return;
      r.needed++; draw(); save(`${r.label} : ${r.needed}`);
    });
    for (const btn of $$<HTMLButtonElement>(el, '[data-dec]')) btn.addEventListener('click', () => {
      const i = Number(btn.dataset.dec), r = roles[i]!;
      if (r.needed > 1) { r.needed--; draw(); save(`${r.label} : ${r.needed}`); return; }
      if (roles.length === 1) { toast('Garde au moins un besoin pour les matchs à domicile.', 'ko'); return; }
      const pron = labelForms(r.label).plural ? 'les' : labelForms(r.label).fem ? 'la' : 'le';
      openSheet(`Retirer « ${r.label} » ?`, html`
        <div class="bm-sheet">
          <p class="muted">Les matchs à domicile à venir ne ${pron} demanderont plus. Un créneau où quelqu’un est déjà inscrit : on te demandera.</p>
          <button type="button" class="btn btn-primary" id="bm-rm-yes">Retirer</button>
        </div>`, (sheet, close) => {
        $(sheet, '#bm-rm-yes')!.addEventListener('click', () => { roles.splice(i, 1); close(); draw(); save(`${r.label} ${agreeNoun(r.label, 'retiré')}`); });
      });
    });
    $(el, '#bm-add-need')!.addEventListener('click', () => openAddNeed(roles.map(r => r.label), (label, needed) => {
      roles.push({ label, needed }); draw(); save(`${label} ${agreeNoun(label, 'ajouté')}`);
    }));
    for (const btn of $$<HTMLButtonElement>(el, '[data-unit]')) btn.addEventListener('click', () => {
      const u = units.find(x => x.key === btn.dataset.unit);
      if (u) openUnit(u, roles, () => void reload());
    });
    if (sug) {
      const answer = async (yes: boolean, btn: HTMLButtonElement) => {
        btn.disabled = true;
        try {
          const r = await answerHint(sug.u.team_id, sug.u.squad_id, sug.kind, yes);
          if (yes && r) {
            const after = [...sug.u.rules.filter(x => x.extra || !sug.labels.some(l => sameLabel(l, x.label))),
              ...sug.labels.map((label, k) => ({ label, mode: 'none' as const, needed: null, only_multi: false, extra: false, position: 50 + k }))];
            openReport(`Règles de ${unitShort(sug.u)} enregistrées`, r, changeSummary(roles, sug.u.rules, after), sug.u.mini, () => void reload());
          } else { toast(`D’accord : plus de suggestion pour ${unitShort(sug.u)}.`); await reload(); }
        } catch { btn.disabled = false; toast('Impossible pour le moment.', 'ko'); }
      };
      $<HTMLButtonElement>(el, '#bm-sug-yes')!.addEventListener('click', e => void answer(true, e.currentTarget as HTMLButtonElement));
      $<HTMLButtonElement>(el, '#bm-sug-no')!.addEventListener('click', e => void answer(false, e.currentTarget as HTMLButtonElement));
    }
  };
  draw();
}

/** « ＋ Ajouter un besoin » : son nom (proposés : ceux qui manquent), combien par match. */
function openAddNeed(existing: string[], onAdd: (label: string, needed: number) => void): void {
  let n = 1;
  openSheet('Ajouter un besoin', html`
    <form class="bm-sheet" id="bm-f-need" novalidate>
      <label class="field"><span>Le besoin</span><input name="label" maxlength="60" list="bm-need-labels" placeholder="ex. 24 secondes, arbitre, accueil" autocomplete="off"></label>
      <datalist id="bm-need-labels">${NEED_LABELS.filter(l => !existing.some(x => sameLabel(x, l))).map(l => html`<option value="${l}"></option>`)}</datalist>
      <div class="bm-rule-head"><span>Combien par match ?</span><span id="bm-n">${stepper(n, 1, 'n', 'Nombre')}</span></div>
      <p class="form-msg" id="bm-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Ajouter</button>
    </form>`, (sheet, close) => {
    const step = () => {
      mount($(sheet, '#bm-n')!, stepper(n, 1, 'n', 'Nombre'));
      $(sheet, '[data-dec]')?.addEventListener('click', () => { n = Math.max(1, n - 1); step(); });
      $(sheet, '[data-inc]')?.addEventListener('click', () => { n = Math.min(20, n + 1); step(); });
    };
    step();
    $<HTMLFormElement>(sheet, '#bm-f-need')!.addEventListener('submit', e => {
      e.preventDefault();
      const label = ($<HTMLInputElement>(sheet, '[name="label"]')!.value ?? '').trim();
      const msg = $(sheet, '#bm-msg')!;
      if (!label) { msg.textContent = 'Écris le besoin (ex. 24 secondes).'; return; }
      if (existing.some(x => sameLabel(x, label))) { msg.textContent = 'Ce besoin y est déjà.'; return; }
      close(); onAdd(label, n);
    });
  });
}

// -----------------------------------------------------------------------------
// La fiche d'une équipe engagée
// -----------------------------------------------------------------------------
interface RuleRow { label: string; club: number; mode: RuleMode; count: number; multi: boolean }
interface ExtraRow { label: string; count: number; multi: boolean }

function openUnit(u: UnitView, roles: ClubNeed[], onDone: () => void): void {
  const rows: RuleRow[] = roles.map(c => {
    const r = ruleOf(u.rules, c.label);
    const mode: RuleMode = r && !r.extra ? r.mode : 'club';
    return { label: c.label, club: c.needed, mode, count: mode === 'count' ? (r?.needed ?? c.needed) : c.needed, multi: !!(r && !r.extra && r.only_multi) };
  });
  const extras: ExtraRow[] = u.rules.filter(r => r.extra && !roles.some(c => sameLabel(c.label, r.label))).map(r => ({ label: r.label, count: r.needed ?? 1, multi: r.only_multi }));
  const youth = /^U\s?\d/i.test(u.category ?? '');
  const condOk = (label: string, multi: boolean) => multi || (youth && !OFFICIAL.test(label));
  const short = unitShort(u);
  const left = u.upcoming - u.hand;
  const cond = (key: string, on: boolean) => html`<label class="check bm-cond"><input type="checkbox" data-multi="${key}" ${on ? 'checked' : ''}><span>Seulement s’il y a 2 matchs ou plus au gymnase ce jour-là</span></label>`;
  const ruleBlock = (r: RuleRow, i: number) => {
    const why = ruleWhy(r.label, r.mode, r.multi, u.lk, u.level);
    return html`
      <div class="bm-rule" data-guide="bm-rule">
        <div class="bm-rule-head"><strong>${r.label}</strong>${r.mode === 'count' ? stepper(r.count, 1, `r${i}`, r.label)
          : html`<span class="muted small">${r.mode === 'none' ? 'aucun' : `${r.club} (club)`}</span>`}</div>
        <div class="seg bm-seg" role="group" aria-label="${r.label}">
          <button type="button" data-mode="${i}:club" aria-pressed="${String(r.mode === 'club')}">Club · ${r.club}</button>
          <button type="button" data-mode="${i}:count" aria-pressed="${String(r.mode === 'count')}">Autre</button>
          <button type="button" data-mode="${i}:none" aria-pressed="${String(r.mode === 'none')}">Aucun</button>
        </div>
        ${r.mode !== 'none' && condOk(r.label, r.multi) ? cond(`r${i}`, r.multi) : ''}
        ${why ? html`<p class="bm-why">${why}</p>` : ''}
      </div>`;
  };
  const extraBlock = (x: ExtraRow, i: number) => html`
    <div class="bm-rule">
      <div class="bm-rule-head"><strong>${x.label}</strong><span class="bm-extra-head">${stepper(x.count, 1, `x${i}`, x.label)}<button type="button" class="bm-rm" data-xrm="${i}" aria-label="Retirer ${x.label}">×</button></span></div>
      ${condOk(x.label, x.multi) ? cond(`x${i}`, x.multi) : ''}
      <p class="bm-why">${x.multi ? ruleWhy(x.label, 'count', true, u.lk, u.level) : `Pour ${short} seulement.`}</p>
    </div>`;

  openSheet(unitName(u), html`<div class="bm-sheet" id="bm-unit"></div>`, (sheet, close) => {
    const box = $(sheet, '#bm-unit')!;
    let adding = false;
    const draw = () => {
      mount(box, html`
        <p class="muted small">${[levelText(u), homeCount(u.upcoming, u.mini)].filter(Boolean).join(' · ')}</p>
        ${rows.map(ruleBlock)}
        ${extras.map(extraBlock)}
        ${adding ? html`<div class="bm-add-row"><input id="bm-x-label" maxlength="60" list="bm-x-labels" placeholder="ex. Accueil" aria-label="Le besoin" autocomplete="off">
            <button type="button" class="btn btn-ghost btn-sm" id="bm-x-ok">Ajouter</button></div>
            <datalist id="bm-x-labels">${NEED_LABELS.filter(l => ![...rows, ...extras].some(x => sameLabel(x.label, l))).map(l => html`<option value="${l}"></option>`)}</datalist>`
          : html`<button type="button" class="btn btn-ghost btn-sm bm-add" id="bm-x-add">＋ Un besoin pour ${short} seulement</button>`}
        <div class="bm-foot">
          <button type="button" class="btn btn-primary" id="bm-save" data-guide="bm-save">Enregistrer</button>
          <p class="hint">${left > 0 ? `S’applique aux ${homeCount(left, u.mini)} ${deName(short)}.` : `S’appliquera aux prochains ${matchWord(u.mini, 2)} à domicile ${deName(short)}.`}${u.hand ? ` ${u.hand > 1 ? `${u.hand} ${matchWord(u.mini, 2)} modifiés à la main gardent leurs besoins.` : `Un ${matchWord(u.mini)} modifié à la main garde ses besoins.`}` : ''}</p>
        </div>`);
      for (const b of $$<HTMLButtonElement>(box, '[data-mode]')) b.addEventListener('click', () => {
        const [i, mode] = b.dataset.mode!.split(':') as [string, RuleMode];
        const r = rows[Number(i)]!;
        if (mode === 'count' && r.mode !== 'count') r.count = r.club;
        r.mode = mode; draw();
      });
      for (const b of $$<HTMLButtonElement>(box, '[data-inc], [data-dec]')) b.addEventListener('click', () => {
        const key = b.dataset.inc ?? b.dataset.dec!, d = b.dataset.inc ? 1 : -1;
        const t = key[0] === 'r' ? rows[Number(key.slice(1))]! : extras[Number(key.slice(1))]!;
        t.count = Math.min(20, Math.max(1, t.count + d)); draw();
      });
      for (const c of $$<HTMLInputElement>(box, '[data-multi]')) c.addEventListener('change', () => {
        const key = c.dataset.multi!;
        (key[0] === 'r' ? rows[Number(key.slice(1))]! : extras[Number(key.slice(1))]!).multi = c.checked; draw();
      });
      for (const b of $$<HTMLButtonElement>(box, '[data-xrm]')) b.addEventListener('click', () => { extras.splice(Number(b.dataset.xrm), 1); draw(); });
      $(box, '#bm-x-add')?.addEventListener('click', () => { adding = true; draw(); $<HTMLInputElement>(box, '#bm-x-label')?.focus(); });
      $(box, '#bm-x-ok')?.addEventListener('click', () => {
        const label = $<HTMLInputElement>(box, '#bm-x-label')!.value.trim();
        if (!label) return;
        if ([...rows, ...extras].some(x => sameLabel(x.label, label))) { toast('Ce besoin y est déjà : règle-le au-dessus.', 'ko'); return; }
        extras.push({ label, count: 1, multi: false }); adding = false; draw();
      });
      $<HTMLButtonElement>(box, '#bm-save')!.addEventListener('click', async e => {
        const btn = e.currentTarget as HTMLButtonElement;
        const after = storedRules([
          ...rows.map((r, i) => ({ label: r.label, mode: r.mode, needed: r.mode === 'count' ? r.count : null, only_multi: r.mode !== 'none' && r.multi, extra: false, position: i })),
          ...extras.map((x, i) => ({ label: x.label, mode: 'count' as const, needed: x.count, only_multi: x.multi, extra: true, position: 100 + i })),
        ]);
        btn.disabled = true;
        try {
          const r = await saveUnitRules(u.team_id, u.squad_id, after);
          close();
          openReport(`Règles de ${short} enregistrées`, r, changeSummary(roles, u.rules, after), u.mini, onDone);
        } catch { btn.disabled = false; toast('Enregistrement impossible. Réessaie.', 'ko'); }
      });
    };
    draw();
  });
}

/** Après « Enregistrer » : ce qui a changé, et les créneaux déjà pris qu'une règle aurait retirés (rien en silence). */
export function openReport(title: string, r: ApplyReport, summary: string, mini: boolean, onDone: () => void): void {
  const lines = [r.matches ? `${cap(homeCount(r.matches, mini))} mis à jour.` : `Aucun ${matchWord(mini)} à domicile à venir pour l’instant : la règle vaudra pour les prochains.`];
  if (summary) lines.push(summary.endsWith('.') ? summary : `${summary}.`);
  if (r.hand) lines.push(r.hand > 1 ? `${r.hand} ${matchWord(mini, 2)} modifiés à la main gardent leurs besoins.` : `Un ${matchWord(mini)} modifié à la main garde ses besoins.`);
  const card = (k: KeptSlot, i: number) => {
    const w = keptWho(k.people);
    return html`
      <div class="bm-box" data-kept="${i}">
        <p class="eyebrow">Un créneau déjà pris</p>
        <p><strong>${k.label} · ${k.title}</strong><br><span class="muted small">${longDay(k.date)} · ${w.line}</span></p>
        <div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" data-keep="${i}">Le garder</button><button type="button" class="btn btn-ghost btn-sm" data-drop="${i}">${w.action}</button></div>
      </div>`;
  };
  openSheet(title, html`
    <div class="bm-sheet">
      <ul class="bm-done">${lines.map(l => html`<li><span>${l}</span></li>`)}</ul>
      ${r.kept.map(card)}
      <button type="button" class="btn btn-primary" data-close>Compris</button>
    </div>`, sheet => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-keep]')) b.addEventListener('click', () => {
      const k = r.kept[Number(b.dataset.keep)]!;
      mount($(sheet, `[data-kept="${b.dataset.keep}"]`)!, html`<p class="muted small">Gardé : ${k.label} · ${k.title}. ${keptWho(k.people).line}.</p>`);
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-drop]')) b.addEventListener('click', async () => {
      const k = r.kept[Number(b.dataset.drop)]!;
      b.disabled = true;
      try {
        await dropSlot(k.slot_id);
        const who = names(k.people.map(p => firstOf(p.name)));
        mount($(sheet, `[data-kept="${b.dataset.drop}"]`)!, html`<p class="muted small">Retiré : ${k.label} · ${k.title}. ${who} ${k.people.length > 1 ? 'ont' : 'a'} reçu un merci.</p>`);
      } catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  }, onDone);
}

// -----------------------------------------------------------------------------
// Sur la carte d'un match : « Modifier les besoins », « Inscrire quelqu'un », un prénom à toucher
// -----------------------------------------------------------------------------
/** Les gestes du bureau sur les cartes de Créneaux (volunteer.ts les pose). `onDone` : après un changement. */
export function wireNeedsTools(root: HTMLElement, ctx: Ctx, needs: VolunteerNeed[], onDone: () => void): void {
  for (const b of $$<HTMLButtonElement>(root, '[data-needs]')) b.addEventListener('click', () =>
    void openMatchNeeds(needs.filter(n => n.source === 'match' && n.event_id === b.dataset.needs), onDone));
  for (const b of $$<HTMLButtonElement>(root, '[data-enrol]')) b.addEventListener('click', () => {
    const n = needs.find(x => x.slot_id === b.dataset.enrol);
    if (n) void openEnrol(ctx, n, onDone);
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-who]')) b.addEventListener('click', () => {
    const n = needs.find(x => x.slot_id === b.dataset.who);
    if (n) void openThank(n, Number(b.dataset.whoI ?? 0), onDone);
  });
}

/** « Besoins de ce match » : le nombre de chaque besoin (jamais sous les inscrits), × pour le retirer (avec un merci
 *  s'il y a des inscrits), « ＋ Ajouter un besoin ». Seulement ce match ; il garde ses besoins quand une règle change. */
export async function openMatchNeeds(slots: VolunteerNeed[], onDone: () => void): Promise<void> {
  const f = slots[0];
  if (!f) return;
  const [plan, people] = await Promise.all([
    matchPlan(f.event_id).catch(() => null),
    Promise.all(slots.map(s => (s.taken ? slotPeople(s.slot_id).catch(() => [] as SlotPerson[]) : Promise.resolve([] as SlotPerson[])))),
  ]);
  const today = localDate(new Date());
  interface Row { slot_id: string | null; label: string; needed: number; taken: number; names: string[]; who: SlotPerson[]; asking: boolean; gone: boolean }
  const rows: Row[] = slots.map((s, i) => ({ slot_id: s.slot_id, label: s.label, needed: s.needed, taken: s.taken, names: s.names, who: people[i] ?? [], asking: false, gone: false }));
  const sub = (r: Row) => (r.taken ? `${r.names.join(', ')} ${agree(genderOf(r.who), 'inscrit', 'inscrite', r.taken)}`
    : !r.slot_id || (plan && !plan.planned.some(p => sameLabel(p, r.label))) ? 'ajouté pour ce match' : 'personne encore');
  const firstNames = (r: Row) => names(r.names.map(firstOf));
  openSheet('Besoins de ce match', html`
    <div class="bm-sheet">
      <p><strong>${f.title}</strong><br><span class="muted small">${whenLine(f)}${f.location ? ` · ${f.location}` : ''}</span></p>
      <ul class="bm-edit" id="bm-rows" data-guide="bm-match-rows"></ul>
      <div id="bm-add-slot"></div>
      <p class="hint">Seulement ce match. Un besoin où quelqu’un est inscrit : on te demande avant de le retirer, et la personne est prévenue. Les règles ${plan?.unit ? deName(plan.unit) : 'de l’équipe'} : Club › Bénévolat › Besoins des matchs.</p>
      <button type="button" class="btn btn-primary" id="bm-save-match">Enregistrer</button>
    </div>`, (sheet, close) => {
    const ul = $(sheet, '#bm-rows')!, add = $(sheet, '#bm-add-slot')!;
    let adding = false;
    const draw = () => {
      mount(ul, html`${rows.map((r, i) => r.gone ? '' : r.asking ? html`
        <li class="bm-ask">
          <span>${firstNames(r)} ${r.taken > 1 ? 'recevront' : 'recevra'} un merci : « ${thankMessage(firstOf(r.names[0] ?? ''), r.label, 'plus_besoin', f.occurrence_date, today).body} »</span>
          <span class="btn-row"><button type="button" class="btn btn-primary btn-sm" data-yes="${i}">Retirer et prévenir</button><button type="button" class="btn btn-ghost btn-sm" data-no="${i}">Garder</button></span>
        </li>` : html`
        <li><span class="bm-w"><strong>${r.label}</strong><span class="muted small">${sub(r)}</span></span>
          ${stepper(r.needed, Math.max(1, r.taken), String(i), r.label)}<button type="button" class="bm-rm" data-rm="${i}" aria-label="Retirer ${r.label}">×</button></li>`)}`);
      mount(add, adding ? html`<div class="bm-add-row"><input id="bm-s-label" maxlength="60" list="bm-s-labels" placeholder="ex. Accueil" aria-label="Le besoin" autocomplete="off">
          <button type="button" class="btn btn-ghost btn-sm" id="bm-s-ok">Ajouter</button></div>
          <datalist id="bm-s-labels">${NEED_LABELS.filter(l => !rows.some(x => !x.gone && sameLabel(x.label, l))).map(l => html`<option value="${l}"></option>`)}</datalist>`
        : html`<button type="button" class="btn btn-ghost btn-sm bm-add" id="bm-s-add">＋ Ajouter un besoin</button>`);
      for (const b of $$<HTMLButtonElement>(ul, '[data-inc], [data-dec]')) b.addEventListener('click', () => {
        const r = rows[Number(b.dataset.inc ?? b.dataset.dec)]!;
        r.needed = Math.min(20, Math.max(Math.max(1, r.taken), r.needed + (b.dataset.inc ? 1 : -1))); draw();
      });
      for (const b of $$<HTMLButtonElement>(ul, '[data-rm]')) b.addEventListener('click', () => {
        const r = rows[Number(b.dataset.rm)]!;
        if (r.taken) r.asking = true; else r.gone = true;
        draw();
      });
      for (const b of $$<HTMLButtonElement>(ul, '[data-yes]')) b.addEventListener('click', () => { const r = rows[Number(b.dataset.yes)]!; r.asking = false; r.gone = true; draw(); });
      for (const b of $$<HTMLButtonElement>(ul, '[data-no]')) b.addEventListener('click', () => { rows[Number(b.dataset.no)]!.asking = false; draw(); });
      $(add, '#bm-s-add')?.addEventListener('click', () => { adding = true; draw(); $<HTMLInputElement>(add, '#bm-s-label')?.focus(); });
      $(add, '#bm-s-ok')?.addEventListener('click', () => {
        const label = $<HTMLInputElement>(add, '#bm-s-label')!.value.trim();
        if (!label) return;
        const back = rows.find(x => x.gone && sameLabel(x.label, label));
        if (back) back.gone = false;
        else if (rows.some(x => sameLabel(x.label, label))) { toast('Ce besoin y est déjà.', 'ko'); return; }
        else rows.push({ slot_id: null, label, needed: 1, taken: 0, names: [], who: [], asking: false, gone: false });
        adding = false; draw();
      });
    };
    draw();
    $<HTMLButtonElement>(sheet, '#bm-save-match')!.addEventListener('click', async e => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      try {
        const r = await saveMatchNeeds(f.event_id, rows.filter(x => !x.gone).map(x => ({ slot_id: x.slot_id, label: x.label, needed: x.needed })));
        close();
        toast(`Besoins du match enregistrés${r.thanked ? ` · ${r.thanked} ${r.thanked > 1 ? 'personnes prévenues' : 'personne prévenue'}` : ''}`);
        onDone();
      } catch { btn.disabled = false; toast('Enregistrement impossible. Réessaie.', 'ko'); }
    });
  });
}

/** « Inscrire quelqu'un » : un membre qui l'a dit de vive voix. Les comptes du club, leur rôle, ce qu'ils font déjà
 *  ce jour-là ; la personne est prévenue et peut se désister. */
export async function openEnrol(ctx: Ctx, n: VolunteerNeed, onDone: () => void): Promise<void> {
  const list = await slotCandidates(n.slot_id).catch(() => null);
  if (!list) { toast('Impossible pour le moment.', 'ko'); return; }
  const cands = [...list].sort((a, b) => b.done - a.done || a.name.localeCompare(b.name, 'fr'));
  const today = localDate(new Date());
  const left = Math.max(0, n.needed - n.taken);
  const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const row = (c: Candidate) => html`
    <li data-name="${fold(c.name)}"><button type="button" data-pick="${c.user_id}"><span class="pick-main"><strong>${c.name}</strong><span class="muted small">${c.caps}</span>
      ${c.busy.map(b => { const l = busyLine(b); return l.warn ? html`<span class="pick-warn">${l.text}</span>` : html`<span class="muted small">${l.text}</span>`; })}
      ${c.minor ? html`<span class="muted small">Ses parents sont prévenus</span>` : ''}</span></button></li>`;
  openSheet(`Inscrire ${labelForms(n.label).a}`, html`
    <div class="bm-sheet">
      <p><strong>${n.title}</strong><br><span class="muted small">${whenLine(n)} · ${left} ${left > 1 ? 'places' : 'place'}</span></p>
      <input class="search" type="search" id="bm-q" placeholder="Chercher un prénom…" aria-label="Chercher un prénom" autocomplete="off">
      ${cands.length ? html`<ul class="pick-list bm-pick" data-guide="bm-pick">${cands.map(row)}</ul>` : html`<p class="muted small">Tous les comptes du club sont déjà sur ce créneau.</p>`}
      <p class="muted small" id="bm-none" hidden>Personne à ce prénom dans le club.</p>
      ${guestBlock()}
      <p class="hint">La personne est prévenue (« ${addedMessage(n.label, n.occurrence_date, today, false, false)} ») et peut se désister. Le créneau compte pour ses points comme si elle l’avait pris.</p>
    </div>`, (sheet, close) => {
    const q = $<HTMLInputElement>(sheet, '#bm-q')!;
    q.addEventListener('input', () => {
      const v = fold(q.value.trim());
      let shown = 0;
      for (const li of $$<HTMLLIElement>(sheet, '.bm-pick li')) { const on = !v || (li.dataset.name ?? '').includes(v); li.hidden = !on; if (on) shown++; }
      $(sheet, '#bm-none')!.hidden = shown > 0 || !v;
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-pick]')) b.addEventListener('click', async () => {
      const c = cands.find(x => x.user_id === b.dataset.pick)!;
      b.disabled = true;
      try { await addSignup(n.slot_id, c.user_id); close(); toast(`C’est noté : ${c.name} a reçu un message.`); onDone(); }
      catch (e) { b.disabled = false; toast(problem(e, 'Inscription impossible.'), 'ko'); }
    });
    wireGuest(sheet, n.slot_id, () => { close(); onDone(); });
    void ctx;
  });
}

/** « <Prénom> · <besoin> » : retirer avec un merci, « Déjà comblé » ou « Plus besoin ». Le message part tout seul. */
export async function openThank(n: VolunteerNeed, index: number, onDone: () => void): Promise<void> {
  const people = await slotPeople(n.slot_id).catch(() => [] as SlotPerson[]);
  const name = n.names[index];
  const p = (people[index]?.name === name ? people[index] : undefined) ?? people.find(x => x.name === name);
  if (!p) { toast('Impossible pour le moment.', 'ko'); return; }
  if (p.guest) { openGuestRemove(n, p, onDone); return; }
  const first = firstOf(p.name);
  const today = localDate(new Date());
  const since = new Date(p.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  let reason: ThankReason = 'comble';
  openSheet(`${first} · ${lowerFirst(n.label)}`, html`
    <div class="bm-sheet">
      <p><strong>${n.title}</strong><br><span class="muted small">${whenLine(n)} · ${agree(p.g === 'F' ? 'F' : 'M', 'inscrit', 'inscrite')}${p.by_bureau ? ' par le bureau' : ''} le ${since}</span></p>
      <div class="seg" role="group" aria-label="Pourquoi" id="bm-why">${(['comble', 'plus_besoin'] as ThankReason[]).map(r => html`<button type="button" data-why="${r}" aria-pressed="${String(r === reason)}">${REASON_LABEL[r]}</button>`)}</div>
      <div class="bm-box"><p class="eyebrow">${first} reçoit</p><p id="bm-msg"></p></div>
      <button type="button" class="btn btn-primary" id="bm-thank">Retirer avec ce merci</button>
      <p class="hint">Rien à écrire : le message part tout seul, court. Le créneau ne compte pas dans ses points.${p.minor ? ' Ses parents sont prévenus aussi.' : ''}</p>
    </div>`, (sheet, close) => {
    const show = () => {
      const m = thankMessage(first, n.label, reason, n.occurrence_date, today, p.vous);
      mount($(sheet, '#bm-msg')!, html`<strong>${m.title}</strong><br>${m.body}`);
      for (const b of $$<HTMLButtonElement>(sheet, '[data-why]')) b.setAttribute('aria-pressed', String(b.dataset.why === reason));
    };
    show();
    for (const b of $$<HTMLButtonElement>(sheet, '[data-why]')) b.addEventListener('click', () => { reason = b.dataset.why as ThankReason; show(); });
    $<HTMLButtonElement>(sheet, '#bm-thank')!.addEventListener('click', async e => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      try { await thankSignup(n.slot_id, p.user_id, reason); close(); toast(`Retiré : ${first} a reçu son merci.`); onDone(); }
      catch { btn.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  });
}

// -----------------------------------------------------------------------------
// La fiche de match : les bénévoles du match, en lecture (coach), avec le chemin des réglages (bureau)
// -----------------------------------------------------------------------------
export async function renderMatchVolunteers(el: HTMLElement, ctx: Ctx, clubId: string, eventId: string, date: string, bureau: boolean): Promise<void> {
  const needs = (await clubVolunteerNeeds(clubId, date, date).catch(() => [] as VolunteerNeed[])).filter(n => n.source === 'match' && n.event_id === eventId);
  if (!needs.length) { mount(el, html``); return; }
  const taken = needs.reduce((a, x) => a + Math.min(x.taken, x.needed), 0), needed = needs.reduce((a, x) => a + x.needed, 0);
  mount(el, html`
    <section class="block bm-vol" data-guide="ev-benevoles">
      <div class="block-head"><h3>Bénévoles du match</h3><span class="chip ${taken >= needed ? 'chip-ok' : 'chip-warn'}">${taken}/${needed}</span></div>
      <ul>${needs.map(x => html`<li class="${x.taken >= x.needed ? 'full' : ''}"><strong>${x.label}</strong><span class="muted small">${x.names.join(', ') || '—'} · ${x.taken}/${x.needed}</span></li>`)}</ul>
      <p class="hint">${bureau ? 'Un seul match : « Modifier les besoins » sur sa carte (onglet Bénévolat). Les règles de l’équipe : Club › Bénévolat › Besoins des matchs.'
        : 'Le bureau règle les besoins du match ; ici, tu vois qui vient.'}</p>
    </section>`);
  void ctx;
}
